import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  CircularProgress, TextField, Chip, InputAdornment,
  IconButton, Button, Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AddIcon from '@mui/icons-material/Add';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import DialogShell from '../components/design-system/DialogShell';
import PacienteCamposForm from '../components/PacienteCamposForm';
import { validarCamposPaciente } from '../utils/validarPaciente';
import { fieldSx, labelSx, estiloTabla, btnPrimary } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

const FORM_VACIO = {
  nombre: '', apellido: '', dni: '', email: '', telefono: '',
  fecha_nacimiento: '', direccion: '', obra_social: '', numero_afiliado: ''
};

const PUEDE_CORREGIR_DNI = ['admin', 'recepcionista'];

export default function Pacientes() {
  const navigate = useNavigate();
  const rol = sessionStorage.getItem('rol');
  const [pacientes, setPacientes]         = useState([]);
  const [totalResultados, setTotalResultados] = useState(0);
  const [pagina, setPagina]               = useState(1);
  const [totalPaginas, setTotalPaginas]   = useState(0);
  const [obrasSociales, setObrasSociales] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [buscar, setBuscar]               = useState('');
  const [dialogOpen, setDialogOpen]       = useState(false);
  const [editando, setEditando]           = useState(null);
  const [form, setForm]                   = useState(FORM_VACIO);
  const [guardando, setGuardando]         = useState(false);
  const [error, setError]                 = useState('');
  const [errores, setErrores]             = useState({});

  const [dniDialogOpen, setDniDialogOpen] = useState(false);
  const [dniNuevo, setDniNuevo]           = useState('');
  const [dniMotivo, setDniMotivo]         = useState('');
  const [dniError, setDniError]           = useState('');
  const [dniGuardando, setDniGuardando]   = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {});
    setLoading(false);
  }, []);

  async function cargar(termino, paginaBuscada = 1) {
    if (!termino || termino.trim().length < 3) {
      setPacientes([]); setTotalResultados(0); setTotalPaginas(0); setPagina(1);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/pacientes`, { params: { buscar: termino.trim(), page: paginaBuscada } });
      setPacientes(r.data.data || []);
      setTotalResultados(r.data.total || 0);
      setTotalPaginas(r.data.totalPages || 0);
      setPagina(r.data.page || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  function handleBuscar(e) {
    const val = e.target.value;
    setBuscar(val);
    if (val.length === 0 || val.length >= 3) cargar(val, 1);
  }

  function handleCambiarPagina(e, nuevaPagina) {
    cargar(buscar, nuevaPagina);
  }

  function abrirNuevo() { setEditando(null); setForm(FORM_VACIO); setError(''); setErrores({}); setDialogOpen(true); }
  function abrirEditar(p) {
    setEditando(p);
    setForm({
      nombre: p.nombre || '', apellido: p.apellido || '', dni: p.dni || '',
      email: p.email || '', telefono: p.telefono || '',
      fecha_nacimiento: p.fecha_nacimiento ? String(p.fecha_nacimiento).split('T')[0] : '',
      direccion: p.direccion || '', obra_social: p.obra_social || '', numero_afiliado: p.numero_afiliado || '',
    });
    setError(''); setErrores({}); setDialogOpen(true);
  }
  function cerrar() { setDialogOpen(false); }

  async function guardar() {
    const { valido, errores: err } = validarCamposPaciente(form);
    setErrores(err);
    if (!valido) { setError('Revisá los campos marcados: son todos obligatorios.'); return; }
    setGuardando(true); setError('');
    try {
      const payload = { ...form, nombre: form.nombre.trim(), apellido: form.apellido.trim() };
      if (editando) { await axios.put(`${API}/api/pacientes/${editando.id}`, payload); }
      else          { await axios.post(`${API}/api/pacientes`, payload); }
      cerrar(); cargar(buscar, pagina);
    } catch (err) { setError(err.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  }

  // El backend nunca borra el registro físicamente (turnos, HC y auditoría dependen de él) —
  // esto desactiva al paciente (activo=0), igual que médicos y especialidades.
  async function desactivar(p) {
    if (!window.confirm(`¿Desactivar al paciente ${p.apellido}, ${p.nombre}? Va a dejar de aparecer en las búsquedas, pero su historial y turnos se conservan.`)) return;
    try {
      await axios.delete(`${API}/api/pacientes/${p.id}`);
      cargar(buscar, pagina);
    } catch (err) { alert(err.response?.data?.error || 'Error al desactivar'); }
  }

  function abrirCorregirDni() {
    setDniNuevo(''); setDniMotivo(''); setDniError('');
    setDniDialogOpen(true);
  }
  function cerrarCorregirDni() { setDniDialogOpen(false); }

  async function confirmarCorreccionDni() {
    if (!dniNuevo.trim()) { setDniError('El DNI nuevo es obligatorio'); return; }
    if (!dniMotivo.trim()) { setDniError('El motivo es obligatorio'); return; }
    if (!window.confirm(`¿Confirmás cambiar el DNI de ${editando.dni || '(sin DNI)'} a ${dniNuevo.trim()}?`)) return;
    setDniGuardando(true); setDniError('');
    try {
      await axios.put(`${API}/api/pacientes/${editando.id}/corregir-dni`, {
        dni_nuevo: dniNuevo.trim(), motivo: dniMotivo.trim(),
      });
      setForm(f => ({ ...f, dni: dniNuevo.trim() }));
      setEditando(e => ({ ...e, dni: dniNuevo.trim() }));
      cerrarCorregirDni();
      cargar(buscar, pagina);
    } catch (err) { setDniError(err.response?.data?.error || 'No se pudo corregir el DNI'); }
    finally { setDniGuardando(false); }
  }

  function formatFecha(f) {
    if (!f) return '—';
    return String(f).split('T')[0].split('-').reverse().join('/');
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>Pacientes</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            size="small"
            placeholder="Buscar por nombre, DNI o email..."
            value={buscar}
            onChange={handleBuscar}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'var(--c-acento)', fontSize: 18 }} /></InputAdornment>,
              style: { color: '#e0e0e0' }
            }}
            sx={{ width: { xs: '100%', sm: 300 }, ...fieldSx }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}
            sx={{ ...btnPrimary, whiteSpace: 'nowrap' }}>
            Nuevo paciente
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress sx={{ color: 'var(--c-acento)' }} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper} sx={estiloTabla}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
                  <TableCell>Apellido, Nombre</TableCell>
                  <TableCell>DNI</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Fecha Nac.</TableCell>
                  <TableCell>Obra Social</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pacientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ color: '#b0b0b0', py: 4 }}>
                      {buscar.length >= 3 ? 'Sin resultados para esa búsqueda.' : 'Escribí al menos 3 caracteres para buscar pacientes.'}
                    </TableCell>
                  </TableRow>
                ) : pacientes.map(p => (
                  <TableRow key={p.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{p.apellido ? `${p.apellido}, ${p.nombre}` : p.nombre}</TableCell>
                    <TableCell>{p.dni || '—'}</TableCell>
                    <TableCell>{p.email || '—'}</TableCell>
                    <TableCell>{p.telefono || '—'}</TableCell>
                    <TableCell>{formatFecha(p.fecha_nacimiento)}</TableCell>
                    <TableCell>
                      {p.obra_social ? <>{p.obra_social}{p.numero_afiliado ? ` · ${p.numero_afiliado}` : ''}</> : '—'}
                    </TableCell>
                    <TableCell>
                      {p.pre_registrado
                        ? <Chip label="Pre-registrado" size="small" sx={{ bgcolor: '#1a3a5c', color: 'var(--c-acento)', fontSize: 11 }} />
                        : <Chip label="Activo" size="small" color="success" sx={{ fontSize: 11 }} />}
                    </TableCell>
                    <TableCell align="center">
                      {['admin', 'medico'].includes(rol) && (
                        <IconButton onClick={() => window.open(`/historial/${p.id}`, '_blank')} sx={{ color: '#81c784' }} size="small" title="Historia Clínica">
                          <MedicalInformationIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton onClick={() => abrirEditar(p)} sx={{ color: 'var(--c-acento)' }} size="small" title="Editar">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {rol === 'admin' && (
                        <IconButton onClick={() => desactivar(p)} sx={{ color: '#f48fb1' }} size="small" title="Desactivar">
                          <PersonOffIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPaginas > 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
              <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                {totalResultados} resultados — página {pagina} de {totalPaginas}
              </Typography>
              <Pagination
                count={totalPaginas}
                page={pagina}
                onChange={handleCambiarPagina}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>
      )}

      <DialogShell
        open={dialogOpen}
        onClose={cerrar}
        maxWidth="sm"
        title={editando ? 'Editar paciente' : 'Nuevo paciente'}
        subtitle={editando ? `ID: ${editando.id}` : null}
        actions={
          <>
            <Button onClick={cerrar} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
            <Button variant="contained" onClick={guardar} disabled={guardando} sx={btnPrimary}>
              {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear paciente'}
            </Button>
          </>
        }
      >
        <PacienteCamposForm
          form={form} setForm={setForm} obrasSociales={obrasSociales} autoFocusNombre={!editando}
          errores={errores}
          dniReadOnly={!!editando}
          accionesDni={editando && PUEDE_CORREGIR_DNI.includes(rol) ? (
            <Typography component="button" type="button" onClick={abrirCorregirDni}
              sx={{ fontSize: 11, color: 'var(--c-acento)', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: '4px 0 0', '&:hover': { textDecoration: 'underline' } }}>
              Corregir DNI
            </Typography>
          ) : null}
        />

        {error && <Typography sx={{ color: '#f44336', fontSize: 13, mt: 1.5 }}>{error}</Typography>}
      </DialogShell>

      <DialogShell
        open={dniDialogOpen}
        onClose={cerrarCorregirDni}
        maxWidth="xs"
        title="Corregir DNI"
        subtitle={editando ? `Paciente: ${editando.apellido}, ${editando.nombre}` : null}
        actions={
          <>
            <Button onClick={cerrarCorregirDni} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
            <Button variant="contained" onClick={confirmarCorreccionDni} disabled={dniGuardando} sx={btnPrimary}>
              {dniGuardando ? 'Guardando...' : 'Confirmar corrección'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box>
            <Typography sx={labelSx}>DNI actual</Typography>
            <Typography sx={{ fontSize: 14, color: '#90a4ae' }}>{editando?.dni || '—'}</Typography>
          </Box>
          <Box>
            <Typography sx={labelSx}>DNI nuevo</Typography>
            <TextField fullWidth value={dniNuevo} onChange={e => setDniNuevo(e.target.value)} sx={fieldSx} />
          </Box>
          <Box>
            <Typography sx={labelSx}>Motivo de la corrección *</Typography>
            <TextField fullWidth multiline minRows={2} value={dniMotivo} onChange={e => setDniMotivo(e.target.value)} sx={fieldSx} />
          </Box>
          {dniError && <Typography sx={{ color: '#f44336', fontSize: 13 }}>{dniError}</Typography>}
        </Box>
      </DialogShell>
    </Box>
  );
}
